import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Modal } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { useModalRegistration } from '@stores/ModalStore';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import css from './RenameChatModal.module.scss';

export type RenameChatModalRef = {
  open: () => void;
  close: () => void;
};

interface Props {
  defaultValue: string;
  /** Performs the rename. The modal awaits this promise and closes
   *  on success, so callers should let exceptions bubble for the
   *  modal to keep itself open on failure. */
  onSubmit: (name: string) => Promise<void>;
}

export const RenameChatModal = observer(
  forwardRef<RenameChatModalRef, Props>(({ defaultValue, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useModalRegistration('chat-rename', open);

    const onOpen = () => {
      setNewName(defaultValue);
      setIsSaving(false);
      setOpen(true);
    };
    const onClose = () => {
      if (isSaving) return;
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({ open: onOpen, close: onClose }));

    useEffect(() => {
      if (!open) return;
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = newName.trim();
      if (!trimmed || trimmed === defaultValue) {
        onClose();
        return;
      }
      try {
        setIsSaving(true);
        await onSubmit(trimmed);
        setOpen(false);
      } catch (error) {
        console.error('Chat rename failed:', error);
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className={css.modal} onClick={(e) => e.stopPropagation()}>
          <IconButton
            className={css.closeButton}
            onClick={onClose}
            size="small"
            disabled={isSaving}
          >
            <CloseIcon />
          </IconButton>
          <h2>Rename chat</h2>
          <form onSubmit={handleSubmit}>
            <div className={css.formFieldAndLabel}>
              <label htmlFor="chat-name">Chat name</label>
              <input
                ref={inputRef}
                type="text"
                id="chat-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className={css.input}
                disabled={isSaving}
              />
            </div>
            <div className={css.actionButtons}>
              <ActionButton variant="secondary" onClick={onClose} type="button" disabled={isSaving}>
                Cancel
              </ActionButton>
              <ActionButton type="submit" disabled={isSaving || !newName.trim()}>
                {isSaving ? 'Renaming...' : 'Rename'}
              </ActionButton>
            </div>
          </form>
        </div>
      </Modal>
    );
  }),
);
